/**
 * The chat panel's conversation controller. It owns the message list, the
 * conversation id (so follow-up turns continue the same server-side
 * conversation), and a small state machine — `idle` while waiting for input,
 * `thinking` while a request is in flight, `error` when the last request failed.
 *
 * Sending appends the user's turn, posts to the REAL `POST /v1/chat`, and appends
 * the assistant's answer (with its optional chart + query) on success or an
 * inline error turn on failure. `retry()` re-sends the last question after an
 * error; `reset()` starts a brand-new conversation.
 *
 * `reset()` aborts an in-flight request rather than merely ignoring it: without
 * this, a question asked just before "New conversation" (or the panel closing)
 * could still resolve afterwards and land its answer in the fresh, reset
 * conversation the user is now looking at — a real, reported inconsistency.
 * Every request tracks its own `AbortController`; only the most recent one's
 * continuation is allowed to touch state (`controllerRef.current === controller`),
 * so a superseded request's resolution or rejection is silently dropped.
 *
 * The mount-effect's cleanup deliberately does NOT abort the in-flight
 * request — only `mountedRef`, so a real unmount still drops the result
 * (the guard above already makes that safe) without touching the network
 * call itself. Aborting there was a real, reported bug: React 18
 * `<StrictMode>` (dev builds) double-invokes mount effects — mount, cleanup,
 * mount, synchronously — and a Dashboard "star" click auto-asks a question
 * the instant the chat panel first mounts, so that simulated cleanup used to
 * abort the very request the auto-submit effect had just started, with
 * nothing left to resend it (the auto-submit's own once-per-nonce guard skips
 * the second mount). The result was a generic error card on the very first
 * click, even though the identical question, typed manually once the panel
 * had settled, always worked.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, ApiNetworkError, ApiTimeoutError } from '../../api/errors';
import { useSession } from '../../auth/session';

import type { ChatTurn } from './types';

export type ChatStatus = 'idle' | 'thinking' | 'error';

export interface ChatController {
  messages: ChatTurn[];
  status: ChatStatus;
  conversationId: string | null;
  send: (text: string) => void;
  retry: () => void;
  reset: () => void;
}

/**
 * A turn that failed reads as the assistant admitting it could not answer — a
 * plain, calm line in the conversation, not a red alert card. A failed question
 * is an ordinary outcome of asking a hard one, and the useful next step is
 * almost always to reword it.
 */
const CANNOT_ANSWER_TEXT =
  "Sorry, I'm not able to find the answer for this query. Please try rephrasing it.";

/**
 * The two failures rewording cannot fix, so they must not be described as if it
 * could: the caller's own rate budget (chat allows 30 turns/minute) and an API
 * that was never reached. Telling someone to rephrase a question that was never
 * asked would send them round in circles.
 */
const RATE_LIMITED_TEXT =
  "That's a lot of questions in a short space of time. Please wait a moment, then ask again.";
const UNREACHABLE_TEXT =
  "I couldn't reach your analytics just now. Nothing was changed — please try again.";

/** Which inline line a failed turn gets (see the constants above). */
function failureText(error: unknown): string {
  if (error instanceof ApiError && error.status === 429) return RATE_LIMITED_TEXT;
  if (error instanceof ApiNetworkError || error instanceof ApiTimeoutError) return UNREACHABLE_TEXT;
  return CANNOT_ANSWER_TEXT;
}

let seq = 0;
const uid = (): string => {
  seq += 1;
  return `t${String(seq)}`;
};

export function useChat(): ChatController {
  const { api } = useSession();
  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const [status, setStatus] = useState<ChatStatus>('idle');
  const [conversationId, setConversationId] = useState<string | null>(null);

  const statusRef = useRef<ChatStatus>('idle');
  const convRef = useRef<string | null>(null);
  const lastTextRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setPhase = useCallback((next: ChatStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const ask = useCallback(
    async (text: string): Promise<void> => {
      // Supersede, don't stack: an old in-flight turn (if any) is abandoned
      // rather than left to resolve into a conversation it no longer belongs to.
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setPhase('thinking');
      try {
        const res = await api.chat.ask(text, convRef.current ?? undefined, controller.signal);
        if (!mountedRef.current || controllerRef.current !== controller) return;
        convRef.current = res.conversation_id;
        setConversationId(res.conversation_id);
        setMessages((current) => [
          ...current,
          {
            role: 'assistant',
            id: uid(),
            answer: res.answer,
            chart: res.chart ?? null,
            query: res.query ?? null,
          },
        ]);
        setPhase('idle');
      } catch (error) {
        if (!mountedRef.current || controllerRef.current !== controller) return;
        // This exact controller's own abort — a deliberate cancel (superseded
        // by a newer ask(), or React 18 StrictMode's dev-only mount/cleanup/
        // remount churn aborting the very request an auto-submit effect just
        // started) — is not a real failure; nothing to tell the user.
        if (controller.signal.aborted) return;
        setMessages((current) => [
          ...current,
          { role: 'error', id: uid(), text: failureText(error) },
        ]);
        setPhase('error');
      }
    },
    [api, setPhase],
  );

  const send = useCallback(
    (raw: string): void => {
      const text = raw.trim();
      if (text === '' || statusRef.current === 'thinking') return;
      lastTextRef.current = text;
      setMessages((current) => [...current, { role: 'user', id: uid(), text }]);
      void ask(text);
    },
    [ask],
  );

  const retry = useCallback((): void => {
    const text = lastTextRef.current;
    if (text === null || statusRef.current === 'thinking') return;
    // Drop the trailing error turn(s) before re-asking the same question.
    setMessages((current) => current.filter((turn) => turn.role !== 'error'));
    void ask(text);
  }, [ask]);

  const reset = useCallback((): void => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    convRef.current = null;
    lastTextRef.current = null;
    setConversationId(null);
    setMessages([]);
    setPhase('idle');
  }, [setPhase]);

  return { messages, status, conversationId, send, retry, reset };
}
